//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import LEGACY_TOAST_TEMPLATE from '@legacy-components/common/toast-notifications/templates/legacy-toast.html';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { UploadQueueActions } from '@media-library/redux/upload-queue/uploadQueueSlice';

import _ from "js/underscore-mixins";

const defaultToastOptions = {
  toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
};
const stickyToastOptions = {
  ...defaultToastOptions,
  extendedTimeOut: 0,
  timeOut: 0,
};

(function () {
  const curalate = angular.module('curalate');

  curalate.factory('QueuedUploaderService', [
    'TOASTS',
    'ToastNotificationService',
    '$ngRedux',
    '$templateCache',
    '$q',
    '$rootScope',
    '$window',
    'ProcessingVideosService',
    'SessionVarsService',
    function (
      TOASTS,
      ToastNotificationService,
      $ngRedux,
      $templateCache,
      $q,
      $rootScope,
      $window,
      ProcessingVideosService,
      SessionVarsService,
    ) {
      $templateCache.put(
        'directives/toast/simplebar.html',
        LEGACY_TOAST_TEMPLATE,
      );

      const isLargeVideoUploadEnabled = SessionVarsService.isFeatureEnabled(
        FeatureNames.LargeVideoUpload,
      );
      const maxFileSizeMb = isLargeVideoUploadEnabled ? 2000 : 200;

      function addToastNotification(notification) {
        return $ngRedux.dispatch(
          toastNotificationsActions.addToastNotification(notification),
        );
      }

      function Uploader(config) {
        let currentQueue = null;
        let successToasts = [];
        // Allow toast notification service to be overwritten.
        config.toastNotificationService =
          config.toastNotificationService || ToastNotificationService;

        config.waitForServerResponse = config.waitForServerResponse || false;

        function onFinished(data) {
          if (currentQueue === data.queue) {
            // If we've already started a new queue,
            // we don't need to clear this variable.
            // This can happen in the cancelled queue case.
            currentQueue = null;
          }

          if (data.cancelled) {
            addToastNotification({
              message:
                data.uploads.length > 0
                  ? `${data.uploads.length} items uploaded successfully before cancellation.`
                  : 'The upload has been canceled.',
              options: defaultToastOptions,
              title: 'Upload Canceled',
              type: ToastType.INFO,
            });
          } else if (!data.errored) {
            successToasts.push(
              addToastNotification({
                message: data.hadSuccessfulVideo
                  ? "Almost done! Some videos may take time to process, but we'll let you know when they're ready."
                  : 'Your images are now available! Enjoy!',
                options: data.hadSuccessfulVideo
                  ? stickyToastOptions
                  : defaultToastOptions,
                title: 'Upload Complete!',
                type: ToastType.SUCCESS,
              }),
            );
          } else if (data.uploads.length > 0) {
            successToasts.push(
              addToastNotification({
                message: `${data.uploads.length} items uploaded successfully. ${
                  data.hadSuccessfulVideo
                    ? "Some videos may take time to process, but we'll let you know when they're ready. "
                    : ''
                }Enjoy!`,
                options: data.hadSuccessfulVideo
                  ? stickyToastOptions
                  : defaultToastOptions,
                title: 'Upload Complete!',
                type: ToastType.SUCCESS,
              }),
            );
          }

          if (config.onQueueFinished) {
            config.onQueueFinished(data);
          }
        }

        function onUpdate(notice) {
          if (notice === 'canceled') {
            // The current queue has been cancelled. It will take some time to finalize,
            // but subsequent enqueues should start a new queue.
            currentQueue = null;
          }
        }

        $rootScope.$on('scheduler:videos-finished-processing', () => {
          if (
            successToasts.length &&
            ProcessingVideosService.getCurrentlyProcessingCount() === 0
          ) {
            successToasts.forEach((t) => {
              $ngRedux.dispatch(
                toastNotificationsActions.removeToastNotification(t),
              );
            });
            successToasts = [];
          }
        });

        this.enqueueFiles = function (files) {
          if (!currentQueue) {
            currentQueue = new UploaderQueue({
              uploadDelegate: config.uploadDelegate,
              onItemUploaded: config.onItemUploaded,
              onItemFailed: config.onItemFailed,
              toastNotificationService: config.toastNotificationService,
              waitForServerResponse: config.waitForServerResponse,
            });
            currentQueue.promise.then(onFinished, undefined, onUpdate);
          }
          currentQueue.enqueueFiles(files);
        };

        this.updateToastNotificationService = function (
          newNotificationService,
        ) {
          config.toastNotificationService =
            newNotificationService || ToastNotificationService;
        };
      }

      function UploaderQueue(config) {
        const that = this;
        let uploadingToastId;
        const errors = [];
        let uploadQueue = [];
        let uploadQueueSize = 0;
        let uploadQueueProgress = 0;
        let currentUploadProgress = 0;
        let uploadInProgressPromise;
        const uploadedItems = [];
        let waitingOnUploads = [];
        let queueHadSuccessfulVideo = false;
        let queueCancelled = false;
        let queueErrored = false;
        const queueDeferred = $q.defer();
        // from config
        const uploadDelegate = config.uploadDelegate;

        // normalize callbacks
        config.onItemUploaded = config.onItemUploaded || angular.noop;
        config.onItemFailed = config.onItemFailed || angular.noop;

        config.toastNotificationService = ToastNotificationService;

        function createProgressToast() {
          uploadingToastId = config.toastNotificationService.warning({
            title: 'Uploading',
            options: {
              ...TOASTS.STICKY_TOAST_OPTIONS,
              positionClass: 'toast-bottom-left',
              customIconClass: 'fa fa-refresh fa-lg',
              closeButton: false,
              customGetProgress() {
                return (
                  (100 * (uploadQueueProgress + currentUploadProgress)) /
                  uploadQueueSize
                );
              },
              customOnCancel: cancelUploads,
              customIsCancellable() {
                return !queueCancelled && uploadQueueProgress < uploadQueueSize;
              },
            },
          });
        }

        function beforeUnloadHandler(e) {
          e.returnValue =
            'An upload is still in progress! If you leave now, your upload will be canceled.';
        }

        function startUploading() {
          if (uploadInProgressPromise) {
            throw new Error(
              "Sanity check failed. This uploader shouldn't be started again!",
            );
          }
          $window.addEventListener('beforeunload', beforeUnloadHandler);
          uploadNext();
        }

        function cancelUploads() {
          queueCancelled = true;
          config.toastNotificationService.updateOptions(uploadingToastId, {
            title: 'Canceling Upload',
          });
          if (uploadInProgressPromise) {
            uploadInProgressPromise.abort();
          }
          while (uploadQueue.length) {
            uploadQueueSize -= uploadQueue.pop().size;
          }
          queueDeferred.notify('canceled');
        }

        function finishQueue() {
          if (errors.length > 0) {
            config.onItemFailed(errors);
          }

          config.toastNotificationService.close(uploadingToastId);
          uploadingToastId = null;

          const data = {
            uploads: uploadedItems,
            errors,
            errored: queueErrored,
            hadSuccessfulVideo: queueHadSuccessfulVideo,
            cancelled: queueCancelled,
            queue: that,
          };

          uploadInProgressPromise = null;
          uploadQueueSize = 0;
          currentUploadProgress = 0;
          queueHadSuccessfulVideo = false;

          queueDeferred.resolve(data);
        }

        function uploadNext() {
          let fileSize = 0;
          let uploadComplete = false;
          const waitForServerResponse = config.waitForServerResponse;

          currentUploadProgress = 0;

          if (uploadQueue.length === 0) {
            uploadInProgressPromise = null;
            $window.removeEventListener('beforeunload', beforeUnloadHandler);
            return;
          }

          if (waitForServerResponse && waitingOnUploads.length > 0) {
            return;
          }

          const imageToUpload = uploadQueue.shift();
          waitingOnUploads.push(imageToUpload);
          fileSize = imageToUpload.size;

          if (!uploadingToastId) {
            createProgressToast();
          }

          uploadInProgressPromise = uploadDelegate(imageToUpload);
          uploadInProgressPromise
            .then(
              (image) => {
                if (queueCancelled) {
                  if (!image) {
                    errors.push({
                      name: imageToUpload.name,
                      message: 'Upload Canceled',
                    });
                    $ngRedux.dispatch(
                      UploadQueueActions.removeFailedItem({
                        itemId: imageToUpload.uuid,
                      }),
                    );
                  }
                } else {
                  if (image && image.videoId) {
                    queueHadSuccessfulVideo = true;
                  }
                  uploadedItems.push({
                    input: imageToUpload,
                    output: image,
                  });
                }

                if (imageToUpload.uuid) {
                  $rootScope.$broadcast(
                    `uploader:${imageToUpload.uuid}:complete`,
                  );
                  if (image) {
                    image.uuid = imageToUpload.uuid;
                  }
                }

                if (!uploadComplete) {
                  uploadComplete = true;
                  uploadQueueProgress += fileSize;
                  uploadNext();
                }

                if (image) {
                  config.onItemUploaded(image, uploadedItems.length - 1);
                }
              },
              (error) => {
                if (
                  typeof error === 'string' &&
                  error.indexOf('<title>502 Bad Gateway') !== -1 &&
                  error.indexOf('<p>The proxy server received an invalid') !==
                    -1
                ) {
                  // We have an issue right now where very large uploads (over limit) get a 502 error from our
                  // apache proxy. Since we've been unable to find a solution for that, we'll hack in the correct
                  // error message here.
                  error = `Unable to upload file due to file size limitations (${maxFileSizeMb} MB).`;
                }

                errors.push({
                  name: imageToUpload.name,
                  message: error,
                  uuid: imageToUpload.uuid,
                });

                queueErrored = true;

                console.error(
                  'crl8:',
                  typeof error === 'string' ? error : JSON.stringify(error),
                );
              },
              (progress) => {
                if (uploadComplete) {
                  // Don't need phantom progress events.
                  return;
                }

                if (fileSize !== progress.total) {
                  // The actual upload progress event will give us the total size including headers,
                  // so it's usually larger by a few hundred bytes.
                  // Adjust our numbers so our progress bar doesn't overflow!!
                  uploadQueueSize += progress.total - fileSize;
                  fileSize = progress.total;
                }
                currentUploadProgress = progress.loaded;
                if (imageToUpload.uuid) {
                  $rootScope.$broadcast(
                    `uploader:${imageToUpload.uuid}:progress`,
                    progress,
                  );
                }

                if (progress.type === 'load') {
                  // Upload payload has reached the server, now we are just waiting on server
                  // response. Let's start the next upload.

                  uploadComplete = true;
                  uploadQueueProgress += fileSize;
                  uploadNext();
                }
              },
            )
            .finally(() => {
              waitingOnUploads = _(waitingOnUploads).without(imageToUpload);
              if (uploadQueue.length === 0 && waitingOnUploads.length === 0) {
                finishQueue();
              } else if (
                waitForServerResponse &&
                waitingOnUploads.length === 0
              ) {
                uploadNext();
              }
            });
        }

        this.enqueueFiles = function (files) {
          if (!files || !files.length) {
            return;
          }

          if (queueCancelled) {
            throw new Error(
              'Attempt to queue new items to a canceled uploader queue!',
            );
          }

          uploadQueue = uploadQueue.concat(files);
          uploadQueueSize += files.reduce((acc, cur) => acc + cur.size, 0);
          if (!uploadInProgressPromise) {
            startUploading();
          }
        };
        Object.defineProperty(this, 'promise', {
          value: queueDeferred.promise,
          enumerable: true,
        });
      }

      return {
        createUploader(uploadDelegate) {
          return new Uploader(uploadDelegate);
        },
      };
    },
  ]);
})();
